import { isSupabaseConfigured, supabase } from "./supabaseClient";

export interface AccountProduct {
  productKey: string;
  displayName: string;
  routePath: string;
  coreOfferSku: string;
  owned: boolean;
}

export const fallbackAccountProducts: AccountProduct[] = [
  {
    productKey: "paper-kingdom",
    displayName: "Paper Kingdom",
    routePath: "/PaperKingdom/studio",
    coreOfferSku: "paper-kingdom-fe",
    owned: false,
  },
  {
    productKey: "shop-theme-ai",
    displayName: "Shop Theme AI",
    routePath: "/ShopThemeAI",
    coreOfferSku: "shop-theme-ai-fe",
    owned: false,
  },
];

interface AccountProductRow {
  product_key: string;
  display_name: string;
  route_path: string;
  core_offer_sku: string;
}

export function resolveAccountProducts(
  products: AccountProductRow[],
  activeProductKeys: ReadonlySet<string>,
): AccountProduct[] {
  return products.map((product) => ({
    productKey: product.product_key,
    displayName: product.display_name,
    routePath:
      product.product_key === "paper-kingdom"
        ? "/PaperKingdom/studio"
        : product.route_path,
    coreOfferSku: product.core_offer_sku,
    owned: activeProductKeys.has(product.product_key),
  }));
}

function requireClient() {
  if (!supabase || !isSupabaseConfigured) {
    throw new Error("Supabase is not configured for this build.");
  }
  return supabase;
}

export const accountRepository = {
  configured: isSupabaseConfigured,

  async loadProducts(email: string): Promise<AccountProduct[]> {
    const client = requireClient();
    const normalizedEmail = email.trim().toLowerCase();
    const [productsResult, membershipsResult] = await Promise.all([
      client
        .from("app_products")
        .select("product_key, display_name, route_path, core_offer_sku")
        .eq("active", true)
        .order("display_name"),
      client
        .from("product_memberships")
        .select("product_key")
        .eq("email", normalizedEmail)
        .eq("status", "active"),
    ]);

    if (productsResult.error) throw productsResult.error;
    if (membershipsResult.error) throw membershipsResult.error;

    const activeProductKeys = new Set(
      (membershipsResult.data ?? []).map((row) => row.product_key),
    );

    return resolveAccountProducts(productsResult.data ?? [], activeProductKeys);
  },
};
